import { BaseModel } from '../../Basic/models/base.model';
import { AuditableModel } from '../../Misc/models/auditable.model';
import { chartColors } from '../../Charts/enums/chart-colors.enum';
import { ImageModel } from '../../Misc/models/image.model';
import { labelStringToBrandSocialLinkToMap } from '../enums/brand-social-link-type.enum';
import {
    AiBrandIdentityApi,
    BrandColor,
    BrandCompetitor,
    BrandCustomInfo,
    BrandIdentityApi,
    BrandSocialLink,
    BrandTopic
} from '../../../api/brandIdentity/models/brand-identity.api.model';

export class BrandIdentity extends BaseModel<BrandIdentity, BrandIdentityApi> implements AuditableModel {
    id?: number;
    name: string;
    nameVariations?: string[];
    logo?: ImageModel;
    logoPlaceholder?: string;
    logoPlaceholderColor?: string;
    colors: BrandColor[];
    competitors?: BrandCompetitor[];
    linkedBrand?: BrandIdentity; // only used for creating brand identity for competitors on the FE, and it is not tested for equality
    topics?: BrandTopic[];
    websites?: string[];
    socialLinks?: BrandSocialLink[];
    bio?: string;
    tagline?: string;
    missionStatement?: string;
    products?: string;
    brandVoice?: string;
    keyValuePropositions?: string;
    industry?: string;
    customInfo?: BrandCustomInfo[];
    orderId: number;
    primary?: boolean;

    readonly colorOptions: chartColors[] = [
        chartColors.agprBlue,
        chartColors.agprPurple,
        chartColors.agprLightBlue,
        chartColors.agprOrange,
        chartColors.agprYellow,
        chartColors.agprLightGreen,
        chartColors.agprGreen,
        chartColors.agprTeal,
        chartColors.agprAqua,
        chartColors.agprCoralPink,
        chartColors.agprCrimson,
        chartColors.agprLavender
    ];

    constructor(apiModel?: BrandIdentityApi) {
        super();
        if (apiModel) {
            this.constructFromApiModel(apiModel);
        } else {
            this.constructDefault();
        }
    }
    /**
     * This method converts AI gnerated content to BrandIdentitiyModel
     */
    static constructFromAiApiModel(apiModel: AiBrandIdentityApi): BrandIdentity {
        const brand = new BrandIdentity();
        brand.name = apiModel.brand_name?.primary;
        brand.nameVariations = [...apiModel.brand_name.variations];
        const imageModel = new ImageModel();
        imageModel.source = apiModel.primary_logo_url;
        brand.logo = imageModel;
        brand.colors = [
            {
                color: apiModel.brand_primary_color_hex,
                favorite: true
            } as BrandColor
        ];
        brand.competitors = apiModel.competitors?.map(item => ({id: null, name: item} as BrandCompetitor)) ?? [];
        brand.topics = [];
        brand.websites = [
            apiModel.brand_website?.primary_url,
            ...(apiModel.brand_website?.other_urls ?? [])
        ].filter(x => !!x);
        brand.socialLinks = apiModel.brand_social_media_profiles?.map(item => {
            return {
                type: labelStringToBrandSocialLinkToMap[item.platform],
                url: item.url
            };
        }) ?? [];
        brand.bio = apiModel.brand_bio;
        brand.tagline = apiModel.brand_tagline;
        brand.missionStatement = apiModel.brand_mission_statement;
        brand.products = apiModel.brand_products_and_services;
        brand.brandVoice = apiModel.brand_voice_and_personality;
        brand.keyValuePropositions = apiModel.brand_key_value_propositions;
        brand.industry = apiModel.industry;
        brand.primary = false;
        return brand;
    }

    constructFromApiModel(apiModel: BrandIdentityApi) {
        this.id = apiModel.id;
        this.name = apiModel.name;
        this.nameVariations = apiModel.nameVariations ? [...apiModel.nameVariations] : [];
        if (apiModel.logo) {
            const logoModel = new ImageModel();
            logoModel.source = apiModel.logo;
            this.logo = logoModel;
        }
        this.colors = apiModel.colors
            ? apiModel.colors
                .map(item => ({...item} as BrandColor))
                .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
            : [];
        this.competitors = apiModel.competitors ? apiModel.competitors.map(item => ({...item} as BrandCompetitor)) : [];
        this.topics = apiModel.topics
            ? apiModel.topics
                .map(item => ({...item} as BrandTopic))
                .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
            : [];
        this.websites = apiModel.websites ? [...apiModel.websites] : [];
        this.socialLinks = apiModel.socialLinks
            ? apiModel.socialLinks
                .map(item => ({...item} as BrandSocialLink))
                .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
            : [];
        this.bio = apiModel.bio;
        this.tagline = apiModel.tagline;
        this.missionStatement = apiModel.missionStatement;
        this.products = apiModel.products;
        this.brandVoice = apiModel.brandVoice;
        this.keyValuePropositions = apiModel.keyValuePropositions;
        this.industry = apiModel.industry;
        this.customInfo = apiModel.customInfo
            ? apiModel.customInfo
                .map(item => ({...item} as BrandCustomInfo))
                .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
            : [];
        this.orderId = apiModel.orderId;
        this.primary = apiModel.primary;
        this.setLogoPlaceholder();
    }

    constructDefault() {
        this.id = null;
        this.name = '';
        this.nameVariations = [];
        this.logo = null;
        this.colors = [];
        this.competitors = [];
        this.topics = [];
        this.websites = [];
        this.socialLinks = [];
        this.bio = '';
        this.tagline = '';
        this.missionStatement = '';
        this.products = '';
        this.brandVoice = '';
        this.keyValuePropositions = '';
        this.industry = '';
        this.customInfo = [];
        this.orderId = 0;
        this.primary = false;
        this.setLogoPlaceholder();
    }

    toApiModel(): BrandIdentityApi {
        const apiModel: BrandIdentityApi = {
            id: this.id,
            name: this.name,
            nameVariations: [...this.nameVariations],
            logo: this.logo ? this.logo.source : '',
            colors: [...this.colors],
            competitors: [...this.competitors],
            topics: [...this.topics],
            websites: [...this.websites],
            socialLinks: [...this.socialLinks],
            bio: this.bio,
            tagline: this.tagline,
            missionStatement: this.missionStatement,
            products: this.products,
            brandVoice: this.brandVoice,
            keyValuePropositions: this.keyValuePropositions,
            industry: this.industry,
            customInfo: [...this.customInfo],
            orderId: this.orderId,
            primary: this.primary
        };
        return apiModel;
    }

    clone(): BrandIdentity {
        const cloned = new BrandIdentity(this.toApiModel());
        if (this.linkedBrand) {
            cloned.linkedBrand = this.linkedBrand?.clone();
        }
        return cloned;
    }

    equals(other: BrandIdentity): boolean {
        if (!other
            || this.id !== other.id
            || this.name !== other.name
            || !this.isStringArrayEqual(this.nameVariations, other.nameVariations)
            || !(this.logo?.source === other.logo?.source)
            || !this.isColorsEqual(other.colors)
            || !this.isCompetitorsEqual(other.competitors)
            || !this.isTopicsEqual(other.topics)
            || !this.isStringArrayEqual(this.websites, other.websites)
            || !this.isSocialLinksEqual(other.socialLinks)
            || this.bio !== other.bio
            || this.tagline !== other.tagline
            || this.missionStatement !== other.missionStatement
            || this.products !== other.products
            || this.brandVoice !== other.brandVoice
            || this.keyValuePropositions !== other.keyValuePropositions
            || this.industry !== other.industry
            || !this.isCustomInfoEqual(other.customInfo)
            || this.orderId !== other.orderId
            || this.primary !== other.primary
        ) {
            return false;
        }
        return true;
    }

    isStringArrayEqual(value: string[], other: string[]): boolean {
        if (value?.length !== other?.length) {
            return false;
        }

        return value.every(item =>
            other.some(x => x.trim() === item.trim())
        );
    }

    isColorsEqual(other: BrandColor[]): boolean {
        if (this.colors?.length !== other?.length) {
            return false;
        }

        return this.colors.every(item => {
            const thisColor = item.color?.trim()?.toLocaleLowerCase();
            return other.some(
                x => x.color?.trim()?.toLocaleLowerCase() === thisColor && !!x.favorite === !!item.favorite
            );
        });
    }

    isCompetitorsEqual(other: BrandCompetitor[]): boolean {
        if (this.competitors?.length !== other?.length) {
            return false;
        }

        return this.competitors.every(item => {
            const thisName = item.name?.trim()?.toLocaleLowerCase();
            return other.some(
                x => x.id === item.id && x.name?.trim()?.toLocaleLowerCase() === thisName
            );
        });
    }

    isTopicsEqual(other:  BrandTopic[]): boolean {
        if (this.topics?.length !== other?.length) {
            return false;
        }

        return this.topics.every(item =>
            other.some(x => x.topicId === item.topicId && !!x.favorite === !!item.favorite)
        );
    }

    isSocialLinksEqual(other: BrandSocialLink[]): boolean {
        if (this.socialLinks?.length !== other?.length) {
            return false;
        }

        return this.socialLinks.every(item => {
            const thisUrl = item.url?.trim();
            const thisText = item.text?.trim();
            return other.some(
                x => x.type === item.type && x.url?.trim() === thisUrl && x.text?.trim() === thisText
            );
        });
    }

    isCustomInfoEqual(other: BrandCustomInfo[]): boolean {
        if (this.customInfo?.length !== other?.length) {
            return false;
        }

        return this.customInfo.every(item => {
            const thisTitle = item.title?.trim();
            const thisInfo = item.info?.trim();
            return other.some(
                x => x.title?.trim() === thisTitle && x.info?.trim() === thisInfo
            );
        });
    }


    /**
     * Set the Brand Identity placeholder to a 2 character string and its background color
     */
    setLogoPlaceholder(name?: string, colors?: BrandColor[], id?: number) {
        const brandName = (name ?? this.name ?? '').trim();

        if (!brandName) {
            this.logoPlaceholder = '';
        } else {
            const brandNameArr = brandName.split(' ');

            if (brandNameArr.length === 1) {
                // use the first 2 characters of a singular word
                this.logoPlaceholder = brandName.slice(0, Math.min(brandName.length, 2)).toLocaleUpperCase();
            } else {
                // use the first charater of the first and last word of multi words
                this.logoPlaceholder =  brandNameArr[0][0].toLocaleUpperCase() + brandNameArr[brandNameArr.length - 1][0].toLocaleUpperCase();
            }
        }

        const favoriteColor = (colors ?? this.colors ?? []).find(x => x.favorite);
        this.logoPlaceholderColor = favoriteColor
            ? favoriteColor.color
            : (id ?? this.id)
                ? this.colorOptions[(id ?? this.id ?? 0) % this.colorOptions.length]
                : '#ced4da';
    }
}
